import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent } from "react";
import { SuperHero } from "@bluebottlecoffee/design-system/components";
import { CanonicalHead } from "../../../components/CanonicalHead";
import { globalModulesProjection } from "../../../lib/sanity/global-modules";
import sanityClient from "../../../lib/sanity-client";
import { oneClickSubscribeCardDelanguagingQuery } from "../../../lib/sanity/one-click-subscribe-module-queries";
import renderModule from "../../../lib/render-module";
import { toSuperHeroProps } from "../../../lib/transformers/super-hero";
import { MiniCartDialog } from "../../../components/MiniCartDialog";
import { LayoutWrapper } from "../../../components/LayoutWrapper";
import { getDefaultStaticPropsMiniCartWithSlug } from "../../../lib/utils";
import { Page as PageSchema, ShippingCopy } from "../../../lib/sanity-schema";
import {
  cafeGroupFeatureProjection,
  CartBasePropsWithSlug,
} from "../../../lib/sanity/shared";
import { filters, keyof } from "../../../lib/sanity/utils";
import localization from "../../../localization";
import { REGION } from "../../../lib/i18n";
import { isFeatureEnabled } from "../../../lib/utils/is-feature-enabled";
import { quickShopCarouselCardQuery } from "../../../lib/sanity/quick-shop-carousel-queries";
import { shippingCopyQuery } from "../../../lib/sanity/product-queries";
import {
  TranslationGroupSanityType,
  buildTranslationGroupQuery,
  toCopyObject,
} from "../../../lib/sanity/translation-group-queries";
import { ConversionCopy } from "./product/[slug]";
import { useIsClient } from "../../../lib/hooks/use-is-client";
import { resolveTabbedCollections } from "../../../lib/resolve-tabbed-collections";

interface PageData extends CartBasePropsWithSlug {
  pageProps: PageSchema;
  shopcardCopy: {
    copy: ConversionCopy;
    shippingCopy: ShippingCopy;
  };
}

export const getStaticProps: GetStaticProps<PageData> = async (context) => {
  const { props: defaultProps } =
    await getDefaultStaticPropsMiniCartWithSlug(context);
  const { lang, region, slug } = defaultProps;

  const pageProps = await sanityClient().fetch<PageSchema>(`*[
      ${filters.whereType("page")}
      && ${filters.matchSlug<PageSchema>("slug", slug)}
      && ${filters.excludeDrafts}
    ]{
      ...,
      ${keyof<PageSchema>("modules")}[]{
        ...,
        ${globalModulesProjection(lang)},
        ${filters.whereType(
          "cafeGroupFeature",
        )} => ${cafeGroupFeatureProjection},
        ${quickShopCarouselCardQuery(lang)},
        ${oneClickSubscribeCardDelanguagingQuery()}
      }
    }[0]`);

  const conversionCopy: ConversionCopy = await sanityClient()
    .fetch<TranslationGroupSanityType>(
      buildTranslationGroupQuery(lang, "Conversion"),
    )
    .then((translations: TranslationGroupSanityType) =>
      toCopyObject<ConversionCopy>(lang, translations),
    );

  const shippingCopy: ShippingCopy =
    await sanityClient().fetch<ShippingCopy>(shippingCopyQuery);

  await resolveTabbedCollections(pageProps?.modules, { lang, region });

  return {
    props: {
      pageProps,
      shopcardCopy: {
        copy: conversionCopy,
        shippingCopy,
      },
      ...defaultProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = await sanityClient().fetch<string[]>(
    `*[
        ${filters.whereType("page")} &&
        ${filters.excludeDrafts}
    ].slug.current`,
  );

  const params = localization.languages(REGION).flatMap((lang) =>
    slugs.map((slug) => ({
      params: {
        region: REGION,
        lang: lang.code,
        slug,
      },
    })),
  );

  return {
    paths: params,
    fallback: false,
  };
};

function validate(data: PageSchema) {
  if (!data.title) {
    throw Error("Page must have a title.");
  }
}

const Page: FunctionComponent<PageData> = ({
  pageProps,
  shopcardCopy,
  ...defaultProps
}) => {
  validate(pageProps);
  const {
    region,
    lang,
    slug,
    giftCardFormCopy,
    subscribableProducts,
    cart,
    productRecs,
    navAndCartInfoBanner,
    aria,
    appPlayLinks,
  } = defaultProps;
  const { title, metaTags, altBBLogo, hero, modules, isDark } = pageProps;
  const isClient = useIsClient();

  const miniCartDialog = (
    <MiniCartDialog
      productRecs={productRecs}
      giftCardFormCopy={giftCardFormCopy}
      miniCartCopy={cart}
      navAndCartInfoBanner={navAndCartInfoBanner}
      region={region}
      lang={lang}
      subscribableProducts={
        isFeatureEnabled(
          process.env.NEXT_PUBLIC_CART_SUBSCRIPTION_TOGGLE_ENABLED,
        )
          ? subscribableProducts
          : []
      }
    />
  );

  /* Email sign up module is often used as M1. Here we check for that and assign <h1>
  to the module's heading element if true. */
  const transformedModules =
    (!hero || hero?.length === 0) && modules?.[0]._type === "emailSignUp"
      ? [{ ...modules[0], useH1: true }, ...modules.slice(1)]
      : modules;

  return (
    isClient && (
      <>
        <CanonicalHead region={region} lang={lang} slug={slug} />
        <LayoutWrapper
          {...{
            ...defaultProps,
            altBBLogo,
            pageTitle: title[lang],
            metaTags,
            slideOutContent: [miniCartDialog],
            openMiniCartPanel: Boolean(miniCartDialog),
          }}
          isTransparent={!!hero?.length || isDark}
        >
          {hero?.length > 0 && (
            <SuperHero {...toSuperHeroProps(hero, aria, region, lang)} />
          )}
          {transformedModules?.length > 0 &&
            transformedModules.map((module) =>
              renderModule({
                module,
                region,
                lang,
                appPlayLinks,
                shopcardCopy,
                aria,
              }),
            )}
        </LayoutWrapper>
      </>
    )
  );
};

export default Page;
